<template>
    <div class="h-full max-h-full py-3 flex flex-col overflow-y-hidden bg-[#F3F3EF]">
        <div class="px-6 shrink-0 flex items-center justify-between mb-6 md:mt-8 gap-2 flex-wrap">
            <div class="flex gap-2 items-center justify-between grow">
                <div v-if="!showEndRolePlay && nadiaNotesEnabled" class="grow title-subsection text-valence-grey-850"><span class="md:inline hidden">Nadia's</span> Notes</div>
                <button
                    type="button"
                    class="flex items-center justify-center w-10 h-10 bg-white hover:bg-[#F9F9F9] rounded-full border-2 border-[#E8E8E8]"
                    aria-label="Close nadia's notes"
                    @click="$emit('close')">
                    <X foreground-class="fill-valence-grey-750" height="10" width="10" />
                </button>
            </div>
            <BaseButton
                v-if="showEndRolePlay"
                :disabled="!isEndRolePlayEnabled"
                theme="primary"
                class="body-small"
                data-testid="role-play-recap-button-in-widget-list"
                @click="emitter.emit('end_role_play')">
                Create Role Play Recap
            </BaseButton>
            <div v-else-if="showWrapUp" class="flex items-center gap-1 shrink-0">
                <BaseButton v-if="!hideEmailButton" theme="neutral" :disabled="!showWrapUp" @click="emitter.emit(CHAT_EVENT.OPEN_SESSION_SUMMARY)">
                    <i class="bi bi-send" aria-hidden="true"></i>
                    Email
                </BaseButton>
                <BaseButton theme="primary" :disabled="!showWrapUp" @click="emitter.emit(CHAT_EVENT.WRAP_UP_CHAT)"> Wrap Up </BaseButton>
            </div>
        </div>
        <BaseTabs v-model="tabsValue" :tab-ids="tabIds" class="grow overflow-hidden" :unmount-on-hide="false" hide-single-tab-list>
            <template #trigger-notes>
                <div class="flex gap-4 items-center justify-center">
                    <i class="bi bi-journal-text text-2xl" aria-hidden="true"></i>
                    Notes
                </div>
            </template>
            <template #content-notes>
                <div class="p-6 pr-3 flex flex-col gap-6 h-full overflow-y-auto scroll-gutter">
                    <div class="h-0">
                        <ChatNadiaNotes :register-focusable-action="registerFocusableAction" :messages="messages" />
                    </div>
                </div>
            </template>
            <template #trigger-role-play>
                <div class="flex gap-4 items-center justify-center">
                    <div class="w-5 h-5"><CoachingModeMarble :is-loading="false" /></div>
                    Roleplay
                </div>
            </template>
            <template #content-role-play>
                <div class="p-6 pb-0 pr-3 flex flex-col gap-6 h-full overflow-y-auto scroll-gutter">
                    <div class="grow h-0">
                        <ChatRolePlayInsights :messages="messages" :role-play-adjustments="rolePlayAdjustments" :role-play-adjustment-default="rolePlayAdjustmentDefault" />
                    </div>
                </div>
            </template>
        </BaseTabs>
    </div>
</template>

<script setup lang="ts">
import { ACTION, lineIsWidget, pluckAction } from "~vue/chatActions.js";
import ChatRolePlayInsights from "~vue/ChatWidgets/ChatRolePlayInsights.vue";
import { CHAT_EVENT } from "~vue/events";
import X from "~vue/icons/X.vue";
import { computed, inject, nextTick, onMounted, onUnmounted, ref, watch } from "vue";

import type { GlobalProperties } from "./appTypes";
import ChatNadiaNotes from "./ChatWidgets/ChatNadiaNotes.vue";
import BaseButton from "./components/BaseButton.vue";
import BaseTabs from "./components/BaseTabs.vue";
import CoachingModeMarble from "./components/navigation/CoachingModeMarble.vue";
import { useChatStore } from "./stores/chatStore";
import { type ChatMessage, type FocusableActionParams, type FocusWidgetParams, type RolePlayAdjustment, RolePlayAdjustmentEnum, type RolePlayAdjustmentProp } from "./types/chat";

interface Props {
    messages?: ChatMessage[];
    rolePlayAdjustments?: RolePlayAdjustmentProp;
    rolePlayAdjustmentDefault?: RolePlayAdjustment;
    isOpen?: boolean;
    showWrapUp?: boolean;
    visibleWidgetCount?: number;
    showEndRolePlay?: boolean;
    isEndRolePlayEnabled?: boolean;
    hideEmailButton?: boolean;
    nadiaNotesEnabled?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
    messages: () => [],
    rolePlayAdjustments: () => [],
    rolePlayAdjustmentDefault: RolePlayAdjustmentEnum.IS_OK,
    isOpen: false,
    showWrapUp: false,
    visibleWidgetCount: 0,
    showEndRolePlay: false,
    isEndRolePlayEnabled: false,
    hideEmailButton: false,
    nadiaNotesEnabled: false,
});

const emit = defineEmits<{
    expand: [];
    close: [];
}>();

const { emitter } = inject<GlobalProperties>("globalProperties", {} as GlobalProperties);

const actionRefs = ref(new Map<string, () => void>());

onMounted(() => {
    emitter.on(CHAT_EVENT.CHAT_MESSAGE, handleChatMessage);
    emitter.on(CHAT_EVENT.FOCUS_WIDGET, handleFocusWidget);
    emitter.on(CHAT_EVENT.FOCUS_ROLE_PLAY_MESSAGE_FEEDBACK, focusRoleplayTab);
});

onUnmounted(() => {
    emitter.off(CHAT_EVENT.CHAT_MESSAGE, handleChatMessage);
    emitter.off(CHAT_EVENT.FOCUS_WIDGET, handleFocusWidget);
    emitter.off(CHAT_EVENT.FOCUS_ROLE_PLAY_MESSAGE_FEEDBACK, focusRoleplayTab);
});

const NADIA_NOTES_TABS = {
    NOTES: "notes",
    ROLE_PLAY: "role-play",
} as const;

const tabIds = ref<string[]>(props.nadiaNotesEnabled ? [NADIA_NOTES_TABS.NOTES] : []);
const tabsValue = ref(tabIds.value[0]);

const hasRolePlayMonitor = computed(() => pluckAction(props.messages, ACTION.ROLE_PLAY_MONITOR));

interface RolePlay {
    id?: string | number;
    finished_at?: string;
}

watch(
    [hasRolePlayMonitor, () => useChatStore.rolePlay],
    () => {
        const rolePlay = useChatStore.rolePlay as RolePlay;
        const hasActiveRolePlay = rolePlay && rolePlay.finished_at == undefined && rolePlay.id;
        if (hasActiveRolePlay) {
            tabIds.value = [NADIA_NOTES_TABS.ROLE_PLAY];
            tabsValue.value = tabIds.value[0];
        } else if (hasRolePlayMonitor.value) {
            tabIds.value = props.nadiaNotesEnabled ? [NADIA_NOTES_TABS.NOTES, NADIA_NOTES_TABS.ROLE_PLAY] : [NADIA_NOTES_TABS.ROLE_PLAY];
        } else {
            tabIds.value = props.nadiaNotesEnabled ? [NADIA_NOTES_TABS.NOTES] : [];
        }
        if (tabIds.value.length > 0 && !tabIds.value.includes(tabsValue.value)) {
            tabsValue.value = tabIds.value[0];
        }
    },
    { immediate: true },
);

const focusRoleplayTab = () => {
    tabsValue.value = NADIA_NOTES_TABS.ROLE_PLAY;
};

function registerFocusableAction({ messageId, actionName, lineIdx, onFocus }: FocusableActionParams) {
    const key = refNameForWidget(messageId, actionName, lineIdx);
    actionRefs.value.set(key, onFocus);
}

function refNameForWidget(messageId: string | number, actionName: string, lineIdx: number): string {
    return `${actionName}_${messageId}_${lineIdx}`;
}

function handleFocusWidget({ actionName, messageId, lineIdx }: FocusWidgetParams) {
    if (actionName === ACTION.ROLE_PLAY_MONITOR) {
        expandRolePlayMonitor();
        return;
    }

    if (messageId == null || lineIdx == null) {
        return;
    }
    const widgetKey = refNameForWidget(messageId, actionName, lineIdx);
    if (actionRefs.value.has(widgetKey)) {
        const effect = () => {
            actionRefs.value.get(widgetKey)?.();
        };

        if (!props.isOpen) {
            /*
             * Parent component may open the sidebar in the same
             * listener, so we focus the widget in the next tick so that
             * the sidebar UI is shown first.
             */
            nextTick(effect);
        } else {
            effect();
        }
    }
}

function handleChatMessage(socketMessage: { data?: ChatMessage }) {
    const chatMessage = socketMessage.data;

    if (!chatMessage) {
        return;
    }

    if (Array.isArray(chatMessage.lines) && chatMessage.lines.some((l) => lineIsWidget(l))) {
        emitter.emit(CHAT_EVENT.WIDGET_ADDED, chatMessage);
    }
}

function expandRolePlayMonitor() {
    if (!props.isOpen) {
        emit("expand");
    }
    tabsValue.value = NADIA_NOTES_TABS.ROLE_PLAY;
}
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.5s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}

.scroll-gutter {
    scrollbar-gutter: stable;
}
</style>
